import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import {EQUIPMENT} from '../../domain/equipment.value-object';
import type {CreateCustomExerciseInput} from '../ports/exercise-catalog.repository';
import {MuscleIdValidator} from './muscle-id.validator';

export class CreateCustomExerciseDataValidator
  implements Validator<CreateCustomExerciseInput>
{
  private static readonly muscleId = new MuscleIdValidator();

  readonly standardSchema = z
    .object({
      name: z.string().trim().min(1, 'Name is required.').max(120),
      description: z.string().trim().max(2000).nullable(),
      equipment: z.enum(EQUIPMENT),
      primaryMuscleIds: z
        .array(CreateCustomExerciseDataValidator.muscleId.standardSchema)
        .min(1, 'Select at least one primary muscle.')
        .max(8),
      secondaryMuscleIds: z
        .array(CreateCustomExerciseDataValidator.muscleId.standardSchema)
        .max(12),
    })
    .strict()
    .superRefine((value, ctx) => {
      const primary = new Set(value.primaryMuscleIds);
      for (const muscleId of value.secondaryMuscleIds) {
        if (primary.has(muscleId)) {
          ctx.addIssue({
            code: 'custom',
            message: 'A muscle cannot be both primary and secondary.',
            path: ['secondaryMuscleIds'],
          });
          return;
        }
      }
    });

  validate(value: unknown): Result<CreateCustomExerciseInput, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    if (!parsed.success) {
      return err(
        new ValidationError(
          parsed.error.issues[0]?.message ?? 'Invalid create custom exercise data.',
        ),
      );
    }
    return ok(parsed.data);
  }
}
